"use client"

import { useState, useCallback, useMemo, useEffect } from "react"
import { API_ENDPOINTS } from "@/lib/api"
import type { AtsApi } from "@/hooks/ats/use-ats-placements"
import type { AtsClientSyncSetting, AtsSyncSettingsResponse, CanManageSyncSettingsResponse } from "@/types/ats/ats-sync-settings"
import { useCodeValues } from "@/hooks/use-code-values"
import { useAtsSyncSettingsFilters } from "@/hooks/ats/use-ats-sync-settings-filters"
import { getClientRowState } from "@/lib/ats/get-client-row-state"

const EXCLUDED_LOB_CODES = ["PermPlacement", "Expense", "PermPlacement2", "PermPlacement3"]

export function useAtsSyncSettings(atsApi: AtsApi) {
  const codeValues = useCodeValues()
  const [isOpen, setIsOpen] = useState(false)
  const [clients, setClients] = useState<AtsClientSyncSetting[]>([])
  const [draft, setDraft] = useState<Record<number, number[]>>({})
  const [searchText, setSearchText] = useState("")
  const [canManage, setCanManage] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const filters = useAtsSyncSettingsFilters({
    getCodeValuesSortByText: codeValues.getCodeValuesSortByText,
    getRelatedCodeValues: codeValues.getRelatedCodeValues,
    getCodeValueCode: codeValues.getCodeValueCode,
  })

  useEffect(() => {
    atsApi.get<CanManageSyncSettingsResponse>(API_ENDPOINTS.ATS_SERVICE.SYNC_SETTINGS_CAN_MANAGE)
      .then((data) => setCanManage(data.canManage))
      .catch(() => setCanManage(false))
  }, [atsApi])

  const lobs = useMemo(
    () => codeValues.getCodeValuesSortByText("org.CodeLineOfBusiness", true)
      .filter((c) => !EXCLUDED_LOB_CODES.includes(c.code))
      .map((c) => ({ id: c.id, name: c.text })),
    [codeValues],
  )

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await atsApi.get<AtsSyncSettingsResponse>(API_ENDPOINTS.ATS_SERVICE.SYNC_SETTINGS)
      setClients(data.clients)
      setDraft(Object.fromEntries(data.clients.map((c) => [c.id, c.enabledLobIds])) as Record<number, number[]>)
    } catch {
      setError("Failed to load settings.")
    } finally {
      setIsLoading(false)
    }
  }, [atsApi])

  const onOpen = useCallback(() => {
    setSearchText("")
    filters.clear()
    setIsOpen(true)
    load()
  }, [load, filters.clear])

  const onClose = useCallback(() => {
    if (!isSaving) setIsOpen(false)
  }, [isSaving])

  const toggleLob = useCallback((clientId: number, lobId: number) => {
    setDraft((prev) => {
      const current = prev[clientId] ?? []
      const enabled = current.includes(lobId)
      return {
        ...prev,
        [clientId]: enabled ? current.filter((id) => id !== lobId) : [...current, lobId],
      }
    })
  }, [])

  const toggleAll = useCallback(
    (clientId: number) => {
      setDraft((prev) => {
        const current = prev[clientId] ?? []
        const allChecked = lobs.length > 0 && lobs.every((l) => current.includes(l.id))
        return {
          ...prev,
          [clientId]: allChecked ? [] : lobs.map((l) => l.id),
        }
      })
    },
    [lobs],
  )

  const getAllCheckboxState = useCallback(
    (clientId: number): true | false | "indeterminate" => {
      const enabled = draft[clientId] ?? []
      if (enabled.length === 0) return false
      if (enabled.length === lobs.length) return true
      return "indeterminate"
    },
    [draft, lobs],
  )

  const duplicateAtsClientIds = useMemo(() => {
    const seen = new Set<string>()
    const dupes = new Set<string>()
    for (const client of clients) {
      for (const ac of client.atsClients) {
        if (seen.has(ac.id)) dupes.add(ac.id)
        else seen.add(ac.id)
      }
    }
    return dupes
  }, [clients])

  const defaultClient = useMemo(
    () => clients.find((c) => c.isDefault) ?? null,
    [clients],
  )

  const regularClients = useMemo(
    () => clients.filter((c) => !c.isDefault),
    [clients],
  )

  const enabledCount = useMemo(
    () => regularClients.filter((c) => (draft[c.id] ?? []).length > 0).length,
    [regularClients, draft],
  )

  const changedClientIds = useMemo(
    () => new Set(
      clients
        .filter((c) => {
          const original = c.enabledLobIds
          const edited = draft[c.id] ?? []
          return original.length !== edited.length || original.some((id) => !edited.includes(id))
        })
        .map((c) => c.id)
    ),
    [clients, draft],
  )

  const hasChanges = changedClientIds.size > 0

  const filterChips = useMemo(() => {
    const chips: string[] = []
    if (searchText.trim()) chips.push(`"${searchText.trim()}"`)
    if (filters.status === "enabled") chips.push("Enabled")
    else if (filters.status === "disabled") chips.push("Disabled")
    if (filters.warningsOnly) chips.push("With warnings")
    if (filters.unmappedOnly) chips.push("Unmapped")
    if (filters.countryId !== null) {
      const country = filters.countries.find((c) => c.id === filters.countryId)
      if (country) chips.push(country.text)
    }
    if (filters.subdivisionId !== null) {
      const sub = filters.subdivisions.find((s) => s.id === filters.subdivisionId)
      if (sub) chips.push(sub.text)
    }
    if (filters.city.trim()) chips.push(filters.city.trim())
    return chips
  }, [searchText, filters.status, filters.warningsOnly, filters.unmappedOnly, filters.countryId, filters.subdivisionId, filters.city, filters.countries, filters.subdivisions])

  const filteredClients = useMemo(() => {
    let result = regularClients
    if (searchText.trim()) {
      const lower = searchText.toLowerCase()
      // Match the client name OR any mapped ATS client id, so the single search box
      // covers both "find by org name" and "find by ATS id".
      result = result.filter((c) =>
        c.name.toLowerCase().includes(lower) ||
        c.atsClients.some((ac) => ac.id.toLowerCase().includes(lower))
      )
    }
    if (filters.status !== "all") {
      const wantEnabled = filters.status === "enabled"
      result = result.filter((c) => ((draft[c.id] ?? []).length > 0) === wantEnabled)
    }
    if (filters.warningsOnly) {
      result = result.filter((c) => getClientRowState(c.atsClients, draft[c.id] ?? [], duplicateAtsClientIds).warning !== null)
    }
    if (filters.unmappedOnly) {
      result = result.filter((c) => c.atsClients.length === 0)
    }
    if (filters.countryId !== null) {
      result = result.filter((c) => c.countryId === filters.countryId)
    }
    if (filters.subdivisionId !== null) {
      result = result.filter((c) => c.subdivisionId === filters.subdivisionId)
    }
    if (filters.city.trim()) {
      const lower = filters.city.toLowerCase()
      result = result.filter((c) => c.city?.toLowerCase().includes(lower))
    }
    return result
  }, [regularClients, draft, duplicateAtsClientIds, searchText, filters.status, filters.warningsOnly, filters.unmappedOnly, filters.countryId, filters.subdivisionId, filters.city])

  const save = useCallback(async () => {
    setIsSaving(true)
    setError(null)
    try {
      const changedClients = clients.filter((c) => {
        const original = c.enabledLobIds
        const edited = draft[c.id] ?? []
        return original.length !== edited.length || original.some((id) => !edited.includes(id))
      })
      await atsApi.patch(API_ENDPOINTS.ATS_SERVICE.SYNC_SETTINGS, {
        clients: changedClients.map((c) => ({ id: c.id, enabledLobIds: draft[c.id] ?? [] })),
      })
      await load()
    } catch {
      setError("Failed to save settings.")
    } finally {
      setIsSaving(false)
    }
  }, [atsApi, clients, draft, load])

  const applyToAll = useCallback((lobIds: number[]) => {
    setDraft((prev) => {
      const next = { ...prev }
      for (const client of filteredClients) {
        next[client.id] = lobIds
      }
      return next
    })
  }, [filteredClients])

  return {
    canManage,
    isOpen,
    onOpen,
    onClose,
    lobs,
    filters,
    defaultClient,
    duplicateAtsClientIds,
    filteredClients,
    totalClientCount: regularClients.length,
    enabledCount,
    draft,
    setSearchText,
    getAllCheckboxState,
    toggleLob,
    toggleAll,
    save,
    applyToAll,
    filterChips,
    hasChanges,
    changedClientIds,
    isLoading,
    isSaving,
    error,
  }
}
